import type { FeatureAvailability } from "@shared/schema/feature-availability";
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import { useAuth } from "./auth";

export interface AIStatus {
  available: boolean;
  provider: string | null;
}

export type FeatureFlags = {
  tier: string;
  ap: boolean;
  ar: boolean;
  financing: boolean;
  automations: boolean;
  contractToCash: boolean;
  aiReports: boolean;
  aiChat: boolean;
  multiEntity: boolean;
  tasks: boolean;
  communications: boolean;
  activityLog: boolean;
  arInstruments: boolean;
  reconciliation: boolean;
  contractInvoicing: boolean;
  apMatching: boolean;
  customPaymentGateways: boolean;
  paymentGateways: boolean;
  externalApi: boolean;
  directDebit: boolean;
  chartOfAccounts: boolean;
  purchaseOrders: boolean;
  deals: boolean;
  maxEntities: string;
  maxUsers: string;
  planChosen: boolean;
  financingGeoAllowed?: boolean;
  directDebitGeoAllowed?: boolean;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionStatus?: string;
  upgradeOnboardingTier?: string | null;
  downgradeNotice?: {
    previousTier: string;
    timestamp: string;
    pausedAutomations: number;
    archivedEntities: number;
  } | null;
};

/**
 * Flags assumed while `/api/workspace/features` has not answered (yet, or at
 * all). Exported so the surfaces that fall back to it can pin that fallback in
 * tests - see `client/src/components/__tests__/feature-gate.unit.test.tsx`
 * (OCT-11301).
 */
export const DEFAULT_FLAGS: FeatureFlags = {
  tier: "starter",
  ap: true,
  ar: true,
  financing: true,
  automations: true,
  contractToCash: false,
  aiReports: true,
  aiChat: true,
  multiEntity: false,
  tasks: true,
  communications: true,
  activityLog: true,
  arInstruments: false,
  reconciliation: false,
  contractInvoicing: false,
  apMatching: false,
  customPaymentGateways: false,
  paymentGateways: false,
  externalApi: false,
  directDebit: true,
  chartOfAccounts: true,
  purchaseOrders: false,
  deals: false,
  maxEntities: "1",
  maxUsers: "1",
  planChosen: false,
};

const FeaturesContext = createContext<FeatureFlags>(DEFAULT_FLAGS);
const FeaturesLoadingContext = createContext<boolean>(true);
const AIStatusContext = createContext<AIStatus>({ available: true, provider: null });

export function FeaturesProvider({ children }: { children: ReactNode; }) {
  const { user } = useAuth();

  const { data, isLoading: featuresLoading } = useQuery<FeatureFlags>({
    queryKey: ["/api/workspace/features"],
    enabled: !!user,
    staleTime: 60_000,
  });

  const { data: aiStatus } = useQuery<AIStatus>({
    queryKey: ["/api/ai/status"],
    staleTime: 300_000,
  });

  // OCT-3039: feature visibility (nav entries, route gates) is a pure
  // function of the workspace's tier preset / per-workspace overrides served
  // by `/api/workspace/features`. The AI provider's runtime availability
  // (`/api/ai/status`) must NOT silently flip `aiChat` to `false` here -
  // doing so caused Scale-tier workspaces to lose the AI Chat entry in the
  // More menu whenever the provider was unregistered (e.g. ENABLE_REPLIT_AI
  // not set in that env), even though the tier grants `aiChat: true`.
  //
  // If the provider is down, the chat surface itself handles the degraded
  // state: `/api/conversations*` returns `503 AI_DISABLED` via the platform
  // `aiDisabledRouter`, and the chat page surfaces that to the user. The
  // nav entry stays visible so users can discover the feature and see why
  // it's unavailable, rather than the menu silently changing shape.
  const flags = data ?? DEFAULT_FLAGS;

  return (
    <AIStatusContext.Provider value={aiStatus ?? { available: true, provider: null }}>
      <FeaturesLoadingContext.Provider value={!!user && featuresLoading}>
        <FeaturesContext.Provider value={flags}>
          {children}
        </FeaturesContext.Provider>
      </FeaturesLoadingContext.Provider>
    </AIStatusContext.Provider>
  );
}

export function useFeatures(): FeatureFlags {
  return useContext(FeaturesContext);
}

export function useFeaturesLoading(): boolean {
  return useContext(FeaturesLoadingContext);
}

export function useAIStatus(): AIStatus {
  return useContext(AIStatusContext);
}

export function isFinancingAvailable(features: FeatureFlags): boolean {
  return features.financing !== false && features.financingGeoAllowed !== false;
}

export function isDirectDebitAvailable(features: FeatureFlags): boolean {
  return features.directDebit !== false && features.directDebitGeoAllowed !== false;
}

/**
 * Reads the structured feature-availability response (OCT-3319 / OCT-3322).
 *
 * All `<FeatureGate>` features resolve through this hook. The server is the
 * single source of truth for plan-tier and geo restrictions.
 */
export function useFeatureAvailability(feature: string): {
  data: FeatureAvailability | undefined;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const { data, isLoading } = useQuery<FeatureAvailability>({
    queryKey: ["/api/features/availability", feature],
    queryFn: async () => {
      const res = await fetch(
        `/api/features/availability?feature=${encodeURIComponent(feature)}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        throw new Error(`Failed to load feature availability for ${feature}: ${res.status}`);
      }
      return await res.json() as FeatureAvailability;
    },
    enabled: !!user,
    staleTime: 60_000,
  });
  return { data, isLoading };
}
